import { gsap } from '../gsap/setup';
import { isTouchDevice } from '../utils/mediaQueries';

const IDLE_DELAY = 2000;
const IDLE_STAGGER = 0.06;
const TOUCH_THRESHOLD = 45;
const DESKTOP_THRESHOLD = 80;
const RESIZE_HEIGHT_TOLERANCE = 80;

const lerp = (a, b, n) => (1 - n) * a + n * b;

const distance = (x1, y1, x2, y2) => Math.hypot(x2 - x1, y2 - y1);

const getPointerPos = (ev) => {
  if (!ev) {
    ev = window.event;
  }

  const source = ev.touches?.length ? ev.touches[0] : ev;

  return {
    x: source.clientX,
    y: source.clientY,
  };
};

const getMouseDistance = (mousePos, lastMousePos) =>
  distance(mousePos.x, mousePos.y, lastMousePos.x, lastMousePos.y);

const getNewPosition = (position, offset, arr) => {
  const realOffset = Math.abs(offset) % arr.length;

  if (position - realOffset >= 0) {
    return position - realOffset;
  }

  return arr.length - (realOffset - position);
};

function preloadImages(selector) {
  const images = [...document.querySelectorAll(selector)];

  return Promise.all(
    images.map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete) {
            resolve();
            return;
          }

          img.addEventListener('load', resolve, { once: true });
          img.addEventListener('error', resolve, { once: true });
        }),
    ),
  );
}

class TrailImage {
  DOM = {
    el: null,
    inner: null,
  };

  defaultStyle = {
    scale: 1,
    x: 0,
    y: 0,
    opacity: 0,
  };

  timeline = null;
  rect = null;

  constructor(DOM_el, onResize) {
    this.DOM.el = DOM_el;
    this.DOM.inner = this.DOM.el.querySelector('.hero-trail__img-inner');
    this.onResize = onResize;
    this.getRect();
    this.initEvents();
  }

  initEvents() {
    this.resize = (shouldReset = true) => {
      if (shouldReset) {
        gsap.set(this.DOM.el, this.defaultStyle);
      }

      this.getRect();
    };

    this.onResize(this.resize);
  }

  getRect() {
    this.rect = this.DOM.el.getBoundingClientRect();
  }
}

class ImageTrail {
  DOM = { el: null };
  images = [];
  imagesTotal = 0;
  imgPosition = 0;
  zIndexVal = 1;
  activeImagesCount = 0;
  isIdle = true;
  visibleImagesCount = 0;
  threshold = DESKTOP_THRESHOLD;
  visibleImagesTotal = 9;
  mousePos = { x: 0, y: 0 };
  lastMousePos = { x: 0, y: 0 };
  cacheMousePos = { x: 0, y: 0 };
  rafId = null;
  resizeHandlers = [];
  destroyed = false;
  heroEl = null;
  trailStageEl = null;
  interactionEl = null;
  idleTimer = null;
  idleFlushTimeline = null;
  isFlushing = false;
  isTouching = false;
  isTouchMode = false;
  trailIconHidden = false;
  lastViewport = {
    width: window.innerWidth,
    height: window.innerHeight,
  };

  constructor(DOM_el) {
    this.DOM.el = DOM_el;
    this.heroEl = DOM_el.closest('.page-hero');
    this.trailStageEl = DOM_el.closest('.page-hero__trail-stage');
    this.isTouchMode = isTouchDevice();
    this.threshold = this.isTouchMode ? TOUCH_THRESHOLD : DESKTOP_THRESHOLD;
    this.interactionEl = this.isTouchMode && this.trailStageEl ? this.trailStageEl : null;

    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.onPointerMoveEv = this.onPointerMoveEv.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.render = this.render.bind(this);

    this.images = [...this.DOM.el.querySelectorAll('.hero-trail__img')].map(
      (img) =>
        new TrailImage(img, (handler) => {
          const debouncedHandler = () => {
            const width = window.innerWidth;
            const height = window.innerHeight;
            const widthChanged = width !== this.lastViewport.width;
            const heightChanged =
              Math.abs(height - this.lastViewport.height) > RESIZE_HEIGHT_TOLERANCE;
            const shouldReset = widthChanged || heightChanged;

            handler(shouldReset);
            this.lastViewport = { width, height };
          };

          this.resizeHandlers.push(debouncedHandler);
          window.addEventListener('resize', debouncedHandler);
        }),
    );

    this.imagesTotal = this.images.length;
    this.visibleImagesTotal = Math.min(this.visibleImagesTotal, this.imagesTotal - 1);

    if (this.interactionEl) {
      this.interactionEl.addEventListener('touchstart', this.handleTouchStart, {
        passive: true,
      });
      this.interactionEl.addEventListener('touchmove', this.handlePointerMove, {
        passive: true,
      });
      this.interactionEl.addEventListener('touchend', this.handleTouchEnd, {
        passive: true,
      });
      this.interactionEl.addEventListener('touchcancel', this.handleTouchEnd, {
        passive: true,
      });
    } else {
      window.addEventListener('mousemove', this.handlePointerMove);
      window.addEventListener('touchmove', this.handlePointerMove, { passive: true });
      window.addEventListener('mousemove', this.onPointerMoveEv);
      window.addEventListener('touchmove', this.onPointerMoveEv, { passive: true });
    }
  }

  getLocalPos(clientPos) {
    const rect = this.DOM.el.getBoundingClientRect();

    return {
      x: clientPos.x - rect.left,
      y: clientPos.y - rect.top,
    };
  }

  clampLocalPos(localPos) {
    const rect = this.DOM.el.getBoundingClientRect();

    return {
      x: Math.max(0, Math.min(localPos.x, rect.width)),
      y: Math.max(0, Math.min(localPos.y, rect.height)),
    };
  }

  getInteractionZone() {
    if (this.isTouchMode && this.trailStageEl) {
      return this.trailStageEl;
    }

    return this.heroEl;
  }

  isHeroActive() {
    if (!this.heroEl) return true;

    const rect = this.heroEl.getBoundingClientRect();

    return rect.bottom > 0 && rect.top < window.innerHeight;
  }

  isPointerInInteractionZone() {
    if (this.isTouchMode && this.isTouching) return true;

    const zone = this.getInteractionZone();
    if (!zone) return true;

    const rect = zone.getBoundingClientRect();
    const { x, y } = this.mousePos;

    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
  }

  getVisibleImages() {
    return this.images.filter((img) => gsap.getProperty(img.DOM.el, 'opacity') > 0.01);
  }

  cancelIdleFlush() {
    clearTimeout(this.idleTimer);
    this.idleTimer = null;
    this.idleFlushTimeline?.kill();
    this.idleFlushTimeline = null;
    this.isFlushing = false;
  }

  scheduleIdleFlush() {
    if (this.isTouchMode) return;

    clearTimeout(this.idleTimer);
    this.idleTimer = setTimeout(() => this.flushIdleImages(), IDLE_DELAY);
  }

  flushIdleImages() {
    if (this.destroyed || this.isFlushing) return;

    const visible = this.getVisibleImages();

    if (!visible.length) return;

    this.isFlushing = true;
    this.idleFlushTimeline?.kill();

    this.idleFlushTimeline = gsap.timeline({
      onComplete: () => {
        this.isFlushing = false;
        this.visibleImagesCount = 0;
        this.activeImagesCount = 0;
        this.isIdle = true;
        this.zIndexVal = 1;
        this.idleFlushTimeline = null;
      },
    });

    visible.forEach((img, index) => {
      gsap.killTweensOf(img.DOM.el);

      this.idleFlushTimeline.to(
        img.DOM.el,
        {
          duration: 0.4,
          ease: 'power4',
          opacity: 0,
          scale: 1.3,
        },
        index * IDLE_STAGGER,
      );
    });
  }

  handlePointerMove(ev) {
    if (this.destroyed) return;

    this.mousePos = getPointerPos(ev);

    if (this.isTouchMode && !this.rafId) {
      this.startRenderLoop();
    }
  }

  hideTrailIcon() {
    if (this.trailIconHidden || !this.trailStageEl) return;

    this.trailIconHidden = true;
    this.trailStageEl.classList.add('is-interacted');
  }

  handleTouchStart(ev) {
    if (this.destroyed) return;

    this.hideTrailIcon();
    this.isTouching = true;
    this.mousePos = getPointerPos(ev);
    this.lastMousePos = { ...this.mousePos };
    this.cacheMousePos = { ...this.mousePos };
    this.startRenderLoop();
  }

  handleTouchEnd() {
    this.isTouching = false;
  }

  onPointerMoveEv() {
    if (this.destroyed) return;

    this.cacheMousePos = { ...this.mousePos };
    this.startRenderLoop();
    window.removeEventListener('mousemove', this.onPointerMoveEv);
    window.removeEventListener('touchmove', this.onPointerMoveEv);
  }

  startRenderLoop() {
    if (this.rafId) return;

    this.rafId = requestAnimationFrame(this.render);
  }

  stopRenderLoop() {
    if (!this.rafId) return;

    cancelAnimationFrame(this.rafId);
    this.rafId = null;
  }

  render() {
    if (this.destroyed) return;

    const moveDistance = getMouseDistance(this.mousePos, this.lastMousePos);

    this.cacheMousePos.x = lerp(this.cacheMousePos.x || this.mousePos.x, this.mousePos.x, 0.3);
    this.cacheMousePos.y = lerp(this.cacheMousePos.y || this.mousePos.y, this.mousePos.y, 0.3);

    if (moveDistance > this.threshold) {
      this.showNextImage();
      this.lastMousePos = { ...this.mousePos };
    }

    if (this.isIdle && this.zIndexVal !== 1) {
      this.zIndexVal = 1;
    }

    if (this.isTouchMode && !this.isTouching && !this.getVisibleImages().length) {
      this.stopRenderLoop();
      return;
    }

    this.rafId = requestAnimationFrame(this.render);
  }

  showNextImage() {
    if (this.imagesTotal < 2) return;
    if (!this.isHeroActive() || !this.isPointerInInteractionZone()) return;

    this.cancelIdleFlush();

    this.zIndexVal += 1;
    this.imgPosition = this.imgPosition < this.imagesTotal - 1 ? this.imgPosition + 1 : 0;

    const img = this.images[this.imgPosition];
    this.visibleImagesCount += 1;

    gsap.killTweensOf(img.DOM.el);

    const scaleValue = gsap.utils.random(0.5, 1.6);
    const localCache = this.clampLocalPos(this.getLocalPos(this.cacheMousePos));
    const localMouse = this.clampLocalPos(this.getLocalPos(this.mousePos));

    img.timeline = gsap
      .timeline({
        onStart: () => this.onImageActivated(),
        onComplete: () => this.onImageDeactivated(),
      })
      .fromTo(
        img.DOM.el,
        {
          scale: scaleValue - Math.max(gsap.utils.random(0.2, 0.6), 0),
          rotationZ: 0,
          opacity: 1,
          zIndex: this.zIndexVal,
          x: localCache.x - img.rect.width / 2,
          y: localCache.y - img.rect.height / 2,
        },
        {
          duration: 0.4,
          ease: 'power3',
          scale: scaleValue,
          rotationZ: gsap.utils.random(-3, 3),
          x: localMouse.x - img.rect.width / 2,
          y: localMouse.y - img.rect.height / 2,
        },
        0,
      );

    if (this.visibleImagesCount >= this.visibleImagesTotal) {
      const lastInQueue = getNewPosition(
        this.imgPosition,
        this.visibleImagesTotal,
        this.images,
      );
      const queuedImg = this.images[lastInQueue];

      gsap.to(queuedImg.DOM.el, {
        duration: 0.4,
        ease: 'power4',
        opacity: 0,
        scale: 1.3,
        onComplete: () => {
          if (this.activeImagesCount === 0) {
            this.isIdle = true;
          }
        },
      });
    }

    this.scheduleIdleFlush();
  }

  onImageActivated = () => {
    this.activeImagesCount += 1;
    this.isIdle = false;
  };

  onImageDeactivated = () => {
    this.activeImagesCount -= 1;
  };

  destroy() {
    this.destroyed = true;
    this.cancelIdleFlush();
    this.stopRenderLoop();

    window.removeEventListener('mousemove', this.handlePointerMove);
    window.removeEventListener('touchmove', this.handlePointerMove);
    window.removeEventListener('mousemove', this.onPointerMoveEv);
    window.removeEventListener('touchmove', this.onPointerMoveEv);

    if (this.interactionEl) {
      this.interactionEl.removeEventListener('touchstart', this.handleTouchStart);
      this.interactionEl.removeEventListener('touchmove', this.handlePointerMove);
      this.interactionEl.removeEventListener('touchend', this.handleTouchEnd);
      this.interactionEl.removeEventListener('touchcancel', this.handleTouchEnd);
    }

    this.resizeHandlers.forEach((handler) => {
      window.removeEventListener('resize', handler);
    });
    this.resizeHandlers = [];

    this.images.forEach((img) => {
      gsap.killTweensOf(img.DOM.el);
      gsap.set(img.DOM.el, img.defaultStyle);
    });
  }
}

let instances = [];

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function initHeroMotionTrail() {
  destroyHeroMotionTrail();

  if (prefersReducedMotion()) return;

  document.querySelectorAll('.js-hero-trail').forEach((el) => {
    preloadImages('[data-hero-trail-img]').then(() => {
      if (!document.body.contains(el)) return;
      instances.push(new ImageTrail(el));
    });
  });
}

export function destroyHeroMotionTrail() {
  instances.forEach((instance) => instance.destroy());
  instances = [];
}
